import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import type { User } from '@fp3/shared'

import { setSentryUser } from '@/lib/sentry'
import { queryClient } from '@/providers/QueryProvider'
import { clearReplayToken, saveReplayToken } from '@/lib/offline/outboxDb'
import { clearPersistedQueryCache } from '@/lib/offline/queryPersist'
import { AUTH_TOKEN_STORAGE_KEY, AUTH_USER_STORAGE_KEY } from '@/lib/auth/storageKeys'
import { authApi } from '../api/authApi'
import { AuthContext } from './AuthContext'
import type { AuthContextType } from './AuthContextType'

interface AuthProviderProps {
  children: ReactNode
}

const initializeToken = (): string | null => {
  const storedToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
  if (!storedToken) return null

  try {
    const payloadBase64 = storedToken.split('.')[1]
    if (!payloadBase64) return null

    const payload = JSON.parse(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/')))
    if (Date.now() >= payload.exp * 1000) {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
      localStorage.removeItem(AUTH_USER_STORAGE_KEY)
      return null
    }

    return storedToken
  } catch {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(AUTH_USER_STORAGE_KEY)
    return null
  }
}

const initializeUser = (): User | null => {
  const storedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY)
  if (!storedUser) return null

  try {
    return JSON.parse(storedUser)
  } catch (error) {
    console.error('Failed to parse stored user data:', error)
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(AUTH_USER_STORAGE_KEY)
    return null
  }
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(initializeToken)
  const [user, setUser] = useState<User | null>(initializeUser)

  // covers initial load from localStorage, login, and logout
  useEffect(() => {
    setSentryUser(user?.id ?? null)
  }, [user?.id])

  const login: AuthContextType['login'] = (newToken, newUser) => {
    setToken(newToken)
    setUser(newUser)
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, newToken)
    localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(newUser))
    // a previous session on this device may have left cached lookups behind (a crash, or a
    // logout that never ran); they belong to whoever was signed in then, not to this user
    queryClient.clear()
    void clearPersistedQueryCache()
    // mirror the token where the service worker can read it: a background sync runs with
    // no page open, and a worker cannot reach localStorage
    void saveReplayToken(newToken)
  }

  const updateUser: AuthContextType['updateUser'] = (partial) => {
    setUser((current) => {
      if (!current) return current
      const next = { ...current, ...partial }
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }

  const logout: AuthContextType['logout'] = async () => {
    try {
      // call backend to invalidate refresh token
      await authApi.logout()
    } catch (error) {
      // log error but still clear local state
      console.error('Logout API call failed:', error)
    } finally {
      // always clear local state, even if API call fails
      setToken(null)
      setUser(null)
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
      localStorage.removeItem(AUTH_USER_STORAGE_KEY)
      void clearReplayToken()
      queryClient.clear()
      // the cache is per-user and its key has no user in it, so it goes with the session
      void clearPersistedQueryCache()
    }
  }

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    login,
    logout,
    updateUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
